import type { FC, ReactNode } from 'react'
import type { ShellData } from '@/types'

import Link from 'next/link'
import { Clock, Facebook, Instagram, Mail, MapPin, MessageCircle, Music2, Phone, ShieldCheck, Truck, Wallet } from 'lucide-react'
import { useI18n } from '@/lib/i18n'
import { telLink, whatsappLink } from '@/lib/phone'
import { Logo } from '@/components/store/logo'
import { CmsText } from '@/components/store/cms-text'
import { Price } from '@/components/store/price'

interface SiteFooterProps {
    shell: ShellData
}

export const SiteFooter: FC<SiteFooterProps> = ({ shell }): ReactNode => {
    const { tx, txn } = useI18n()
    const { settings, categories } = shell

    const help = [
        { href: '/offers', label: tx('العروض الحالية', 'Current offers') },
        { href: '/shipping', label: tx('الشحن والتوصيل', 'Shipping and delivery') },
        { href: '/track', label: tx('تتبع طلبك', 'Track your order') },
        { href: '/contact', label: tx('تواصل معنا', 'Contact us') }
    ]

    const socials = [
        { href: settings.facebook, label: 'Facebook', icon: Facebook },
        { href: settings.instagram, label: 'Instagram', icon: Instagram },
        { href: settings.tiktok, label: 'TikTok', icon: Music2 }
    ].filter((entry): entry is { href: string, label: string, icon: typeof Facebook } => Boolean(entry.href))

    return (
        <footer className='mt-auto bg-navy-950 text-white'>
            <div className='border-b border-white/10'>
                <div className='container-page grid gap-4 py-8 sm:grid-cols-3'>
                    <div className='flex items-center gap-3'>
                        <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-white/5 text-cyan-300 ring-1 ring-white/10'>
                            <Truck className='size-5' aria-hidden='true' />
                        </span>
                        <div className='flex flex-col'>
                            <span className='text-sm font-semibold'>{tx('شحن لكل المحافظات', 'Shipping to every governorate')}</span>
                            <span className='text-xs text-white/60'>
                                {settings.freeShippingEnabled && settings.freeShippingThreshold > 0
                                    ? txn('مجاناً للطلبات فوق {amount}', 'Free on orders over {amount}', { amount: <Price value={settings.freeShippingThreshold} /> })
                                    : tx('تكلفة الشحن تظهر قبل إتمام الطلب', 'Shipping fee shown before you order')}
                            </span>
                        </div>
                    </div>

                    <div className='flex items-center gap-3'>
                        <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-white/5 text-cyan-300 ring-1 ring-white/10'>
                            <Wallet className='size-5' aria-hidden='true' />
                        </span>
                        <div className='flex flex-col'>
                            <span className='text-sm font-semibold'>{tx('طريقة الدفع', 'Payment')}</span>
                            {settings.paymentNote
                                ? <CmsText table='site_settings' id={settings.id} field={settings.paymentNote} className='text-xs text-white/60' />
                                : <span className='text-xs text-white/60'>{tx('يتم تأكيد الطلب معك قبل الشحن', 'We confirm every order with you before shipping')}</span>}
                        </div>
                    </div>

                    <div className='flex items-center gap-3'>
                        <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-white/5 text-cyan-300 ring-1 ring-white/10'>
                            <ShieldCheck className='size-5' aria-hidden='true' />
                        </span>
                        <div className='flex flex-col'>
                            <span className='text-sm font-semibold'>{tx('جملة وقطاعي', 'Wholesale and retail')}</span>
                            <span className='text-xs text-white/60'>{tx('السعر الأنسب يُطبَّق تلقائياً حسب الكمية', 'The best price applies automatically by quantity')}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className='container-page grid gap-10 py-12 md:grid-cols-12'>
                <div className='flex flex-col gap-4 md:col-span-4'>
                    <Logo name={settings.storeName} settingsId={settings.id} tone='light' />

                    {settings.tagline.v && (
                        <CmsText table='site_settings' id={settings.id} field={settings.tagline} as='p' className='max-w-xs text-sm leading-relaxed text-white/65' />
                    )}

                    {socials.length > 0 && (
                        <div className='flex items-center gap-2'>
                            {socials.map(social => (
                                <a key={social.label} href={social.href} target='_blank' rel='noopener noreferrer' aria-label={social.label} className='flex size-10 items-center justify-center rounded-full bg-white/5 text-white/80 ring-1 ring-white/10 transition-colors hover:bg-white/10 hover:text-white'>
                                    <social.icon className='size-4.5' aria-hidden='true' />
                                </a>
                            ))}
                        </div>
                    )}
                </div>

                <div className='flex flex-col gap-4 md:col-span-3'>
                    <h2 className='text-sm font-semibold text-white'>{tx('الأقسام', 'Categories')}</h2>
                    <ul className='flex flex-col gap-2.5'>
                        {categories.map(category => (
                            <li key={category.id}>
                                <Link href={`/categories/${category.slug}`} className='text-sm text-white/65 transition-colors hover:text-cyan-300'>
                                    <CmsText table='categories' id={category.id} field={category.name} />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='flex flex-col gap-4 md:col-span-2'>
                    <h2 className='text-sm font-semibold text-white'>{tx('مساعدة', 'Help')}</h2>
                    <ul className='flex flex-col gap-2.5'>
                        {help.map(item => (
                            <li key={item.href}>
                                <Link href={item.href} className='text-sm text-white/65 transition-colors hover:text-cyan-300'>{item.label}</Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='flex flex-col gap-4 md:col-span-3'>
                    <h2 className='text-sm font-semibold text-white'>{tx('تواصل معنا', 'Get in touch')}</h2>
                    <ul className='flex flex-col gap-3 text-sm text-white/65'>
                        {settings.whatsapp && (
                            <li>
                                <a href={whatsappLink(settings.whatsapp)} target='_blank' rel='noopener noreferrer' className='inline-flex items-center gap-2.5 transition-colors hover:text-cyan-300' dir='ltr'>
                                    <MessageCircle className='size-4 shrink-0 text-[#25d366]' aria-hidden='true' />
                                    <CmsText table='site_settings' id={settings.id} field={{ v: settings.whatsapp, c: 'whatsapp_number' }} className='tabular-nums' />
                                </a>
                            </li>
                        )}

                        {settings.phone && (
                            <li>
                                <a href={telLink(settings.phone)} className='inline-flex items-center gap-2.5 transition-colors hover:text-cyan-300' dir='ltr'>
                                    <Phone className='size-4 shrink-0' aria-hidden='true' />
                                    <CmsText table='site_settings' id={settings.id} field={{ v: settings.phone, c: 'phone' }} className='tabular-nums' />
                                </a>
                            </li>
                        )}

                        {settings.email && (
                            <li>
                                <a href={`mailto:${settings.email}`} className='inline-flex items-center gap-2.5 break-all transition-colors hover:text-cyan-300'>
                                    <Mail className='size-4 shrink-0' aria-hidden='true' />
                                    <CmsText table='site_settings' id={settings.id} field={{ v: settings.email, c: 'email' }} />
                                </a>
                            </li>
                        )}

                        {settings.address && (
                            <li className='flex items-start gap-2.5'>
                                <MapPin className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                                <CmsText table='site_settings' id={settings.id} field={settings.address} className='leading-relaxed' />
                            </li>
                        )}

                        {settings.workingHours && (
                            <li className='flex items-start gap-2.5'>
                                <Clock className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                                <CmsText table='site_settings' id={settings.id} field={settings.workingHours} className='leading-relaxed' />
                            </li>
                        )}

                        {!settings.whatsapp && !settings.phone && !settings.email && (
                            <li>
                                <Link href='/contact' className='transition-colors hover:text-cyan-300'>{tx('صفحة التواصل', 'Contact page')}</Link>
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            <div className='border-t border-white/10'>
                <div className='container-page flex flex-col items-center justify-between gap-3 py-6 text-xs text-white/50 sm:flex-row'>
                    <p>
                        <span suppressHydrationWarning>© {new Date().getFullYear()}</span>
                        {' '}
                        <CmsText table='site_settings' id={settings.id} field={settings.storeName} />
                    </p>

                    <Link href='/admin' className='transition-colors hover:text-white/80'>
                        {tx('دخول الإدارة', 'Store admin')}
                    </Link>
                </div>
            </div>
        </footer>
    )
}
